import * as THREE from 'three';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';

export type Framing = 'face' | 'upper' | 'body' | 'full';

/** Cadrages : hauteur visée (fraction de sa taille) et distance caméra (m, pour ~1,65 m). */
const FRAMES: Record<Framing, { aim: number; dist: number; elev: number }> = {
  face:  { aim: 0.925, dist: 0.95, elev: 0.03 },
  upper: { aim: 0.8,  dist: 1.75, elev: 0.05 },
  body:  { aim: 0.58, dist: 3.2,  elev: 0.07 },
  full:  { aim: 0.5,  dist: 4.2,  elev: 0.1 },
};

/** Angle cible le plus proche (évite de faire le grand tour). */
function nearestAngle(from: number, to: number) {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return from + d;
}

const ease = (u: number) => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);

/**
 * Mouvements de caméra : transitions douces entre cadrages (en conservant l'azimut choisi par
 * l'utilisateur) et léger bougé "à l'épaule" qui donne vie à l'image sans gêner.
 */
export class CameraDirector {
  height = 1.65;
  handheld = 1;
  /** Moins d'animations demandé par le système : pas de bougé à l'épaule. */
  reducedMotion = false;
  /** Où elle se tient au sol (x, z) : les cadrages visent au-dessus de ce point. */
  focus = new THREE.Vector3();
  /** Hauteur visée imposée selon le cadrage (ex. assise) ; null = d'après sa taille debout. */
  aimOverride: ((f: Framing) => number | null) | null = null;
  /** Fraction verticale de l'écran réellement visible (un panneau peut en masquer le bas). */
  private viewFraction = 1;
  private last: Framing = 'upper';
  private tween?: { t: number; dur: number; fromT: THREE.Vector3; toT: THREE.Vector3; fromD: number; toD: number; fromE: number; toE: number; fromA: number; toA: number };
  private time = 0;
  private sph = new THREE.Spherical();
  private v = new THREE.Vector3();

  constructor(private camera: THREE.PerspectiveCamera, private controls: OrbitControls) {}

  /** Indique la part d'écran libre (mobile : feuille en bas) et recadre en conséquence. */
  setViewFraction(f: number) {
    const v = Math.min(1, Math.max(0.3, f));
    if (Math.abs(v - this.viewFraction) < 0.02) return;
    this.viewFraction = v;
    this.frame(this.last, 0.8);
  }

  /** Cadrage ; `azimuthDeg` (0 = face à elle, + = vers sa gauche) optionnel, sinon azimut actuel conservé. */
  frame(f: Framing, duration = 1.4, azimuthDeg?: number) {
    this.last = f;
    const p = FRAMES[f];
    const offset = this.v.copy(this.camera.position).sub(this.controls.target);
    this.sph.setFromVector3(offset);
    this.tween = {
      t: 0, dur: duration,
      fromT: this.controls.target.clone(),
      toT: new THREE.Vector3(this.focus.x, this.aimOverride?.(f) ?? this.height * p.aim, this.focus.z),
      fromD: this.sph.radius, toD: p.dist * (this.height / 1.65) / Math.max(0.45, this.viewFraction),
      fromE: Math.PI / 2 - this.sph.phi, toE: p.elev,
      fromA: this.sph.theta, toA: azimuthDeg === undefined ? this.sph.theta : nearestAngle(this.sph.theta, THREE.MathUtils.degToRad(azimuthDeg)),
    };
  }

  /** Vers une vue précise (point visé, position de la caméra), en douceur. */
  view(target: THREE.Vector3, position: THREE.Vector3, duration = 1.4) {
    const offset = this.v.copy(this.camera.position).sub(this.controls.target);
    this.sph.setFromVector3(offset);
    const to = new THREE.Spherical().setFromVector3(position.clone().sub(target));
    this.tween = {
      t: 0, dur: duration, fromT: this.controls.target.clone(), toT: target.clone(),
      fromD: this.sph.radius, toD: to.radius, fromE: Math.PI / 2 - this.sph.phi, toE: Math.PI / 2 - to.phi,
      fromA: this.sph.theta, toA: nearestAngle(this.sph.theta, to.theta),
    };
  }

  /** Tourne autour d'elle de `degrees` en `duration` secondes (distance et hauteur conservées). */
  orbit(degrees: number, duration: number) {
    const offset = this.v.copy(this.camera.position).sub(this.controls.target);
    this.sph.setFromVector3(offset);
    const e = Math.PI / 2 - this.sph.phi;
    this.tween = {
      t: 0, dur: duration, fromT: this.controls.target.clone(), toT: this.controls.target.clone(),
      fromD: this.sph.radius, toD: this.sph.radius, fromE: e, toE: e,
      fromA: this.sph.theta, toA: this.sph.theta + THREE.MathUtils.degToRad(degrees),
    };
  }

  get busy() { return !!this.tween; }

  /** Interrompt le mouvement en cours (la caméra reste où elle est). */
  cancel() { this.tween = undefined; }

  /** À appeler APRÈS controls.update(). */
  update(dt: number) {
    this.time += dt;
    if (this.tween) {
      const tw = this.tween;
      tw.t += dt;
      const u = ease(Math.min(1, tw.t / tw.dur));
      this.controls.target.lerpVectors(tw.fromT, tw.toT, u);
      const offset = this.v.copy(this.camera.position).sub(this.controls.target);
      this.sph.setFromVector3(offset);
      this.sph.radius = THREE.MathUtils.lerp(tw.fromD, tw.toD, u);
      this.sph.phi = Math.PI / 2 - THREE.MathUtils.lerp(tw.fromE, tw.toE, u);
      this.sph.theta = THREE.MathUtils.lerp(tw.fromA, tw.toA, u);
      this.camera.position.copy(this.controls.target).add(this.v.setFromSpherical(this.sph));
      this.camera.lookAt(this.controls.target);
      if (tw.t >= tw.dur) this.tween = undefined;
    }
    // Bougé à l'épaule : rotations infimes (quelques centièmes de degré), recalculées à chaque frame.
    if (this.handheld > 0 && !this.reducedMotion) {
      const t = this.time, k = this.handheld * 0.0016;
      this.camera.rotateX((Math.sin(t * 0.71) * 0.6 + Math.sin(t * 1.37 + 1.2) * 0.4) * k);
      this.camera.rotateY((Math.sin(t * 0.53 + 2.1) * 0.6 + Math.sin(t * 1.11 + 0.4) * 0.4) * k);
    }
  }
}
